import { parseLenderDebitResult, parseDebitDataResult } from '@1delta/calldata-sdk'
import { convertLenderUserDataResult } from '../fetch-balances/parse'
import type { LenderToLenderCrossPoolMeta } from '../utils/types'
import type { MergedUserData, PreparedMergedMulticallParams } from './types'

/**
 * Parses the raw multicall results produced by `prepareMergedRpcCalls`
 * or `prepareMergedMulticallParams` into separate token approval,
 * permission, and balance data.
 */
export function parseMergedResult(
  chainId: string,
  rawResults: any[],
  prepared: Pick<
    PreparedMergedMulticallParams,
    | 'tokenApprovalCallCount'
    | 'permissionCallCount'
    | 'extraCallCount'
    | 'tokenApprovalMeta'
    | 'permissionMeta'
    | 'balanceQueries'
  >,
  lenderState: LenderToLenderCrossPoolMeta,
): MergedUserData {
  const {
    tokenApprovalCallCount,
    permissionCallCount,
    extraCallCount,
    tokenApprovalMeta,
    permissionMeta,
    balanceQueries,
  } = prepared

  // Split at the three boundaries
  const taEnd = tokenApprovalCallCount
  const permEnd = taEnd + permissionCallCount
  const extraEnd = permEnd + (extraCallCount ?? 0)

  const tokenApprovalResults = rawResults.slice(0, taEnd)
  const permissionResults = rawResults.slice(taEnd, permEnd)
  const extraResults = rawResults.slice(permEnd, extraEnd)
  const balanceResults = rawResults.slice(extraEnd)

  // Parse each segment with its original parser
  const tokenApprovals =
    tokenApprovalCallCount > 0
      ? parseDebitDataResult({
          raw: tokenApprovalResults,
          tokenAddresses: tokenApprovalMeta.tokenAddresses,
          spenders: tokenApprovalMeta.spenders,
          tokenListForChain: tokenApprovalMeta.tokenListForChain,
        })
      : {}

  const permissions = parseLenderDebitResult({
    metadata: permissionMeta.metadata,
    raw: permissionResults,
    chainId,
  })

  const balances = convertLenderUserDataResult(
    chainId,
    balanceQueries,
    balanceResults,
    lenderState,
  )

  return { tokenApprovals, permissions, balances, extras: extraResults }
}
